import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { ProjectStatus } from '../../../../../../../main/projects/projectsTypes';

import {
  UniversalFilterProjectStatusValue,
  UniversalFilterProjectStatusModes,
  UniversalFilterProjectStatusFilterState,
  UniversalFilterProjectStatusOnFilterStateChange,
  UniversalFilterProjectStatusOnChange
} from '../../UniversalFilterProjectStatus.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterProjectStatusFilterState } from '../useUniversalFilterProjectStatusFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterProjectStatusModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterProjectStatusModes>[] =
  [
    {
      value: UniversalFilterProjectStatusModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterProjectStatusModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterProjectStatusOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterProjectStatusValue;
  onChange: UniversalFilterProjectStatusOnChange;
}

function useUniversalFilterProjectStatus({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterProjectStatusOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterProjectStatusFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterProjectStatusFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterProjectStatusFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          statuses: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterProjectStatusOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterProjectStatusModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        statuses: filterState.statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<(statuses: ProjectStatus[]) => void>(
    (statuses) => {
      handleFilterStateChange({
        mode: filterState.mode,
        statuses: statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterProjectStatus;
